/**
 * The fields of a filing document that `chooseDocument` needs; `charCount` is null when no
 * text was extracted.
 */
export interface ChoosableDocument {
  id: string;
  sequence: number;
  charCount: number | null;
}

/**
 * Which document of a filing is the one on screen, or null when none has text.
 *
 * The reader renders it and the shell scopes Ask to it, so both must ask this: two
 * readings would put a question about the information statement against the cover page.
 *
 * **A citation names the document it was extracted from, and that always wins**: its
 * offsets mean something only in that document. Then an explicit sequence. Then the
 * largest extracted document, which on a Form 10 is the information statement rather than
 * the cover page at sequence 1.
 *
 * @param documents The filing's documents, or undefined before they are fetched.
 * @param named The cited document id and the requested sequence, either absent.
 */
export function chooseDocument<T extends ChoosableDocument>(
  documents: readonly T[] | undefined,
  named: { documentId?: string | null; sequence?: number | null },
): T | null {
  if (!documents) return null;
  // Never a document with no text: there is nothing to read and nothing to ask about.
  const extracted = documents.filter((d) => d.charCount !== null);
  if (extracted.length === 0) return null;

  if (named.documentId) {
    const cited = extracted.find((d) => d.id === named.documentId);
    if (cited) return cited;
  }
  if (named.sequence !== null && named.sequence !== undefined && Number.isInteger(named.sequence)) {
    const asked = extracted.find((d) => d.sequence === named.sequence);
    if (asked) return asked;
  }
  return extracted.reduce((a, b) => ((b.charCount ?? 0) > (a.charCount ?? 0) ? b : a));
}
